import React from 'react';
import { useAppConstantsContext } from '@/context/AppConstants';
import { Box, Stack, Text } from '@/theme/index';
import { ArrowImgContainer, MainImgContainer } from './components';

export interface NoContactResultsProps {
  /** Element responsible of rendering the active filters */
  filters?: React.ReactNode;
  /** Array of filters to be suggested to the user */
  suggestedFilters?: {
    /** Header for the suggested filters */
    header: string;
    /** Element responsible of rendering the suggested filters */
    filters: React.ReactNode;
  }[];
  /** Whether or not the last search returned no results */
  noResultsFound?: boolean;
}

/**
 * Renders the Contact Search default or no results found state.
 * @example
 * ```tsx
 * <NoContactResults
 *   filters={<FiltersComponent />}
 *   suggestedFilters={[
 *     { header: 'Filter 1', filters: <Filter1Component /> },
 *     { header: 'Filter 2', filters: <Filter2Component /> },
 *   ]}
 *   noResultsFound={true}
 * />
 * ```
 */
export function NoContactResults({
  filters,
  suggestedFilters,
  noResultsFound = false,
}: NoContactResultsProps) {
  const {
    images: {
      imgDefaultContactSearchMessage,
      imgNoContactResultsFound,
      imgContactSearchRightArrow,
    },
  } = useAppConstantsContext();

  const DefaultState = (
    <>
      <MainImgContainer
        sx={{
          backgroundImage: `url(${imgDefaultContactSearchMessage})`,
          height: 174,
        }}
      />

      <Text
        variant="h4"
        sx={(theme) => ({
          marginBottom: 0,
          marginTop: { xs: theme.spacing(2), md: 0 },
          marginRight: { xs: theme.spacing(9), sm: 0 },
        })}
        data-testid="no-contact-results-header"
      >
        Begin your search by choosing your criteria here, <br />
        <Text
          component="span"
          fontSize="inherit"
          display={{ xs: 'none', md: 'inline' }}
        >
          or try some of these popular filters.
        </Text>
      </Text>

      <ArrowImgContainer
        sx={{ backgroundImage: `url(${imgContactSearchRightArrow})` }}
      />
    </>
  );

  const NoResultsState = (
    <>
      <MainImgContainer
        sx={{
          backgroundImage: `url(${imgNoContactResultsFound})`,
          height: 100,
        }}
      />

      <Text
        variant="h4"
        sx={(theme) => ({
          marginBottom: 0,
          marginTop: { xs: theme.spacing(2), md: 0 },
        })}
      >
        No results found
      </Text>

      <Text mb={1.5}>Try removing some filters</Text>

      <ArrowImgContainer
        sx={(theme) => ({
          backgroundImage: `url(${imgContactSearchRightArrow})`,
          marginTop: `${theme.spacing(0.5)} !important`,
        })}
      />

      {filters}
    </>
  );

  return (
    <Stack alignItems="center" spacing={{ sm: 2, md: 4 }} position="relative">
      <Stack alignItems="center" textAlign="center">
        {noResultsFound ? NoResultsState : DefaultState}
      </Stack>

      {suggestedFilters && (
        <Stack spacing={{ sm: 2, md: 4 }} display={{ xs: 'none', md: 'flex' }}>
          {suggestedFilters.map((example, index) => (
            <Box key={`suggestion-${index}`}>
              <Text fontWeight="bold" mb={1.5}>
                {example.header}
              </Text>
              {example.filters}
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
